import { useMemo, useState } from "react";
import { Link, useNavigation } from "../app/navigation";
import { useWorkspace } from "../app/workspace";
import { AppShell } from "../components/AppShell";
import { PortfolioThemeControl } from "../components/PortfolioThemeControl";
import { WorkspaceGate } from "../components/WorkspaceGate";
import { unresolvedExceptions } from "../domain/engine";
import { addDaysIso, businessDateKey, isoWeekKey, monthKey } from "../domain/time";
import type { ExceptionCase, WorkspaceState } from "../domain/types";

const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
const roleLabels = {
  owner: "원장",
  manager: "실장",
  instructor: "강사",
  desk: "데스크",
  accounting: "회계",
  read_only: "조회만",
} as const;
const money = new Intl.NumberFormat("ko-KR");

export function ClassesPage() {
  return (
    <WorkspaceGate>
      <Classes />
    </WorkspaceGate>
  );
}

function Classes() {
  const { state, command, mutating, role } = useWorkspace();
  const canManage = role === "owner" || role === "manager" || role === "sample_user";
  const [creating, setCreating] = useState(false);
  const [rosterClassId, setRosterClassId] = useState<string | null>(null);
  const [rosterIds, setRosterIds] = useState<string[]>([]);
  const [form, setForm] = useState({
    name: "",
    subject: "",
    instructorName: "",
    room: "",
    capacity: "12",
    weekdays: [1, 3, 5],
    startTime: "17:00",
    endTime: "18:20",
    compensationKrw: "0",
  });
  async function create() {
    await command({
      type: "class.create",
      name: form.name,
      subject: form.subject,
      instructorId: `local-${form.instructorName.trim().replace(/\s+/g, "-")}`,
      instructorName: form.instructorName,
      room: form.room,
      capacity: Number(form.capacity),
      weekdays: form.weekdays,
      startTime: form.startTime,
      endTime: form.endTime,
      compensationKrw: Number(form.compensationKrw || "0"),
    });
    setCreating(false);
  }
  function openRoster(classId: string, studentIds: string[]) {
    if (rosterClassId === classId) {
      setRosterClassId(null);
      return;
    }
    setRosterClassId(classId);
    setRosterIds([...studentIds]);
  }
  async function saveRoster(classId: string) {
    await command({ type: "class.rosterUpdate", classId, studentIds: rosterIds });
    setRosterClassId(null);
  }
  return (
    <AppShell
      title="수업"
      description="시간표와 명단을 한곳에서 확인합니다"
      actions={
        canManage ? <button
          type="button"
          className="primary-action"
          onClick={() => setCreating((value) => !value)}
        >
          {creating ? "닫기" : "수업 만들기"}
        </button> : null
      }
    >
      {creating ? (
        <section className="editor-panel" aria-labelledby="class-create-title">
          <header>
            <p>충돌 검사 포함</p>
            <h2 id="class-create-title">반복 수업 만들기</h2>
          </header>
          <div className="form-grid">
            <label>
              수업 이름
              <input
                value={form.name}
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
                placeholder="중2 영어 B"
              />
            </label>
            <label>
              과목
              <input
                value={form.subject}
                onChange={(event) =>
                  setForm({ ...form, subject: event.target.value })
                }
                placeholder="영어"
              />
            </label>
            <label>
              강사
              <input
                value={form.instructorName}
                onChange={(event) =>
                  setForm({ ...form, instructorName: event.target.value })
                }
                placeholder="담당 강사"
              />
            </label>
            <label>
              강의실
              <input
                value={form.room}
                onChange={(event) =>
                  setForm({ ...form, room: event.target.value })
                }
                placeholder="1강의실"
              />
            </label>
            <label>
              시작
              <input
                type="time"
                value={form.startTime}
                onChange={(event) =>
                  setForm({ ...form, startTime: event.target.value })
                }
              />
            </label>
            <label>
              종료
              <input
                type="time"
                value={form.endTime}
                onChange={(event) =>
                  setForm({ ...form, endTime: event.target.value })
                }
              />
            </label>
            <label>
              정원
              <input
                type="number"
                min="1"
                max="300"
                value={form.capacity}
                onChange={(event) =>
                  setForm({ ...form, capacity: event.target.value })
                }
              />
            </label>
            <label>
              완료 수업당 수당 후보
              <input
                inputMode="numeric"
                value={form.compensationKrw}
                onChange={(event) =>
                  setForm({
                    ...form,
                    compensationKrw: event.target.value.replace(/[^0-9]/g, ""),
                  })
                }
              />
            </label>
            <fieldset className="weekday-field">
              <legend>요일</legend>
              {[1, 2, 3, 4, 5, 6].map((day) => (
                <label key={day}>
                  <input
                    type="checkbox"
                    checked={form.weekdays.includes(day)}
                    onChange={() =>
                      setForm({
                        ...form,
                        weekdays: form.weekdays.includes(day)
                          ? form.weekdays.filter((value) => value !== day)
                          : [...form.weekdays, day],
                      })
                    }
                  />{" "}
                  {dayNames[day]}
                </label>
              ))}
            </fieldset>
          </div>
          <footer>
            <p>같은 강사 또는 강의실이 같은 시간에 겹치면 저장하지 않습니다.</p>
            <button
              type="button"
              className="primary-action"
              disabled={
                mutating ||
                !form.name.trim() ||
                !form.subject.trim() ||
                !form.instructorName.trim() ||
                !form.room.trim()
              }
              onClick={() => void create()}
            >
              {mutating ? "확인 중…" : "충돌 확인하고 만들기"}
            </button>
          </footer>
        </section>
      ) : null}
      <section className="record-grid" aria-label="수업 목록">
        {state!.classes.map((item) => (
          <article className="record-card" key={item.id}>
            <header>
              <span>{item.subject}</span>
              <strong>{item.name}</strong>
            </header>
            <dl>
              <div>
                <dt>요일</dt>
                <dd>{item.weekdays.map((day) => dayNames[day]).join("·")}</dd>
              </div>
              <div>
                <dt>시간</dt>
                <dd>
                  {item.startTime}–{item.endTime}
                </dd>
              </div>
              <div>
                <dt>담당</dt>
                <dd>{item.instructorName}</dd>
              </div>
              <div>
                <dt>강의실</dt>
                <dd>{item.room}</dd>
              </div>
              <div>
                <dt>명단</dt>
                <dd>
                  {item.studentIds.length}/{item.capacity}명
                </dd>
              </div>
            </dl>
            <footer>
              {canManage ? (
                <button
                  type="button"
                  className="text-action"
                  onClick={() => openRoster(item.id, item.studentIds)}
                >
                  {rosterClassId === item.id ? "명단 편집 닫기" : "명단 편집"}
                </button>
              ) : null}
              <Link
                className="secondary-action"
                href={`/classes/${item.id}/sheet`}
              >
                출결표 열기
              </Link>
            </footer>
            {rosterClassId === item.id ? (
              <section
                className="roster-editor"
                aria-label={`${item.name} 명단 편집`}
              >
                <header>
                  <strong>학생 선택</strong>
                  <span>
                    {rosterIds.length}/{item.capacity}명
                  </span>
                </header>
                {state!.students.length ? (
                  <div className="roster-picker">
                    {state!.students
                      .filter((student) => student.active)
                      .map((student) => (
                        <label key={student.id}>
                          <input
                            type="checkbox"
                            checked={rosterIds.includes(student.id)}
                            onChange={() =>
                              setRosterIds((current) =>
                                current.includes(student.id)
                                  ? current.filter((id) => id !== student.id)
                                  : [...current, student.id],
                              )
                            }
                          />
                          <span>{student.displayName}</span>
                          <small>{student.schoolGrade}</small>
                        </label>
                      ))}
                  </div>
                ) : (
                  <p>먼저 학생을 추가하거나 명단 파일을 가져와 주세요.</p>
                )}
                {rosterIds.length > item.capacity ? (
                  <p className="inline-error" role="alert">
                    정원보다 {rosterIds.length - item.capacity}명 많아요.
                  </p>
                ) : null}
                <button
                  type="button"
                  className="primary-action"
                  disabled={mutating || rosterIds.length > item.capacity}
                  onClick={() => void saveRoster(item.id)}
                >
                  명단과 예정 출결표 저장
                </button>
              </section>
            ) : null}
          </article>
        ))}
      </section>
    </AppShell>
  );
}

export function StudentsPage() {
  return (
    <WorkspaceGate>
      <Students />
    </WorkspaceGate>
  );
}

function Students() {
  const { state, command, mutating, role } = useWorkspace();
  const canManage = role === "owner" || role === "manager" || role === "sample_user";
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    displayName: "",
    schoolGrade: "",
    guardianContactMasked: "",
    billingPlan: "monthly" as "monthly" | "session",
    remainingSessions: "8",
  });
  const students = state!.students.filter((student) =>
    `${student.displayName} ${student.schoolGrade}`
      .toLocaleLowerCase("ko-KR")
      .includes(query.toLocaleLowerCase("ko-KR")),
  );
  async function add() {
    await command({
      type: "student.add",
      displayName: form.displayName,
      schoolGrade: form.schoolGrade,
      guardianContactMasked: form.guardianContactMasked,
      billingPlan: form.billingPlan,
      remainingSessions: Number(form.remainingSessions),
    });
    setAdding(false);
  }
  return (
    <AppShell
      title="학생"
      description={`재원 ${state!.students.filter((item) => item.active).length}명 · 원본 연락처는 목록에서 가립니다`}
      actions={
        canManage ? <>
          <Link className="secondary-action" href="/onboarding/import">
            명단 가져오기
          </Link>
          <button
            type="button"
            className="primary-action"
            onClick={() => setAdding((value) => !value)}
          >
            학생 추가
          </button>
        </> : null
      }
    >
      {adding ? (
        <section className="editor-panel">
          <header>
            <p>한 명 추가</p>
            <h2>학생 기본 정보</h2>
          </header>
          <div className="form-grid">
            <label>
              학생 이름
              <input
                value={form.displayName}
                onChange={(event) =>
                  setForm({ ...form, displayName: event.target.value })
                }
              />
            </label>
            <label>
              학년
              <input
                value={form.schoolGrade}
                onChange={(event) =>
                  setForm({ ...form, schoolGrade: event.target.value })
                }
                placeholder="중1"
              />
            </label>
            <label>
              보호자 연락처 표식
              <input
                value={form.guardianContactMasked}
                onChange={(event) =>
                  setForm({
                    ...form,
                    guardianContactMasked: event.target.value,
                  })
                }
                placeholder="010-••••-1234"
              />
            </label>
            <label>
              수강 방식
              <select
                value={form.billingPlan}
                onChange={(event) =>
                  setForm({
                    ...form,
                    billingPlan: event.target.value as "monthly" | "session",
                  })
                }
              >
                <option value="monthly">월정액</option>
                <option value="session">회차제</option>
              </select>
            </label>
            {form.billingPlan === "session" ? (
              <label>
                시작 회차
                <input
                  type="number"
                  min="0"
                  value={form.remainingSessions}
                  onChange={(event) =>
                    setForm({ ...form, remainingSessions: event.target.value })
                  }
                />
              </label>
            ) : null}
          </div>
          <footer>
            <button
              type="button"
              className="primary-action"
              disabled={mutating || form.displayName.trim().length < 2}
              onClick={() => void add()}
            >
              학생 추가
            </button>
          </footer>
        </section>
      ) : null}
      <div className="toolbar">
        <label className="search-field">
          <span>학생 찾기</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="이름 또는 학년"
          />
        </label>
        <span>{students.length}명 표시</span>
      </div>
      <section className="data-sheet" aria-label="학생 목록">
        <div className="data-sheet__head">
          <span>학생</span>
          <span>수강 반</span>
          <span>수강 방식</span>
          <span>보호자</span>
          <span>기록</span>
        </div>
        {students.map((student) => (
          <article key={student.id}>
            <span>
              <strong>{student.displayName}</strong>
              <small>{student.schoolGrade}</small>
            </span>
            <span>
              {state!.classes
                .filter((item) => student.classIds.includes(item.id))
                .map((item) => item.name)
                .join(", ") || "배정 전"}
            </span>
            <span>
              {student.billingPlan === "session"
                ? `회차제 · ${student.remainingSessions}회`
                : "월정액"}
            </span>
            <span>{student.guardianContactMasked}</span>
            <span>
              <Link
                className="text-action"
                href={`/students/${student.id}/ledger`}
              >
                원장 보기
              </Link>
            </span>
          </article>
        ))}
      </section>
    </AppShell>
  );
}

export function StudentLedgerPage({ studentId }: { studentId: string }) {
  return (
    <WorkspaceGate>
      <StudentLedger studentId={studentId} />
    </WorkspaceGate>
  );
}

function StudentLedger({ studentId }: { studentId: string }) {
  const { state } = useWorkspace();
  const student = state!.students.find((item) => item.id === studentId);
  if (!student)
    return (
      <AppShell title="학생 원장">
        <Empty
          title="학생을 찾지 못했어요"
          body="목록으로 돌아가 다시 선택해 주세요."
          href="/students"
        />
      </AppShell>
    );
  const ledger = state!.ledger
    .filter((item) => item.studentId === student.id)
    .toReversed();
  const attendance = state!.attendance
    .filter(
      (item) => item.studentId === student.id && item.status !== "unmarked",
    )
    .toReversed();
  const makeups = state!.makeups.filter(
    (item) => item.studentId === student.id,
  );
  return (
    <AppShell
      title={`${student.displayName} 원장`}
      description={`${student.schoolGrade} · ${student.billingPlan === "session" ? `남은 ${student.remainingSessions}회` : "월정액"}`}
      actions={
        <Link className="secondary-action" href="/students">
          학생 목록
        </Link>
      }
    >
      <section className="metric-row">
        <Metric
          label="출결 기록"
          value={`${attendance.length}건`}
          note="실제 기록된 수업"
        />
        <Metric
          label="원장 합계"
          value={
            student.billingPlan === "session"
              ? `${ledger.reduce((sum, item) => sum + item.delta, 0)}회`
              : "월정액"
          }
          note="수정은 새 항목으로 남음"
        />
        <Metric
          label="보강 후보"
          value={`${makeups.length}건`}
          note={`${makeups.filter((item) => !item.bookedSessionId && item.eligibility === "eligible").length}건 예약 전`}
        />
      </section>
      <section className="split-ledger">
        <article className="paper-section">
          <header>
            <p>원래 기록을 남기는 정정</p>
            <h2>회차·월정액 원장</h2>
          </header>
          {ledger.length ? (
            <ol className="timeline-list">
              {ledger.map((entry) => (
                <li key={entry.id}>
                  <time>{formatDate(entry.occurredAt)}</time>
                  <div>
                    <strong>{entry.reason}</strong>
                    <span>
                      {entry.kind} · 사건 {entry.id.slice(-8)}
                    </span>
                  </div>
                  <b>
                    {entry.delta > 0 ? "+" : ""}
                    {entry.delta}
                    {entry.balanceAfter === null
                      ? ""
                      : ` · 잔여 ${entry.balanceAfter}`}
                  </b>
                </li>
              ))}
            </ol>
          ) : (
            <EmptyInline text="아직 원장 항목이 없어요." />
          )}
        </article>
        <article className="paper-section">
          <header>
            <p>같은 사건에서 연결</p>
            <h2>출결·보강</h2>
          </header>
          <ol className="timeline-list">
            {attendance.map((record) => (
              <li key={record.id}>
                <time>
                  {record.recordedAt
                    ? formatDate(record.recordedAt)
                    : "기록 전"}
                </time>
                <div>
                  <strong>{attendanceLabel(record.status)}</strong>
                  <span>{record.sessionId}</span>
                </div>
                <b>
                  {record.syncState === "synced" ? "저장됨" : record.syncState}
                </b>
              </li>
            ))}
          </ol>
        </article>
      </section>
    </AppShell>
  );
}

export function ExceptionPage({ exceptionId }: { exceptionId: string }) {
  return (
    <WorkspaceGate roles={["owner", "manager", "sample_user"]}>
      <ExceptionDetail exceptionId={exceptionId} />
    </WorkspaceGate>
  );
}

function ExceptionDetail({ exceptionId }: { exceptionId: string }) {
  const { state, command, mutating } = useWorkspace();
  const { navigate } = useNavigation();
  const [reason, setReason] = useState("");
  const [makeupCancelReason, setMakeupCancelReason] = useState("");
  const exception = state!.exceptions.find((item) => item.id === exceptionId);
  if (!exception)
    return (
      <AppShell title="남은 일">
        <Empty
          title="항목을 찾지 못했어요"
          body="이미 처리됐거나 다른 운영일의 항목일 수 있어요."
          href="/close/today"
        />
      </AppShell>
    );
  const makeup = state!.makeups.find(
    (item) => item.studentId === exception.studentId,
  );
  return (
    <AppShell
      title="남은 일 한 건"
      description={exception.title}
      actions={
        <Link href="/close/today" className="secondary-action">
          오늘 마침으로
        </Link>
      }
    >
      <section className="detail-document">
        <header>
          <span className={`severity-tag severity-tag--${exception.severity}`}>
            {exceptionKind(exception.kind)}
          </span>
          <h2>{exception.title}</h2>
          <p>{exception.summary}</p>
        </header>
        <dl className="cause-ledger">
          <div>
            <dt>원인</dt>
            <dd>{exception.cause}</dd>
          </div>
          <div>
            <dt>규칙</dt>
            <dd>{exception.rule}</dd>
          </div>
          <div>
            <dt>영향</dt>
            <dd>{exception.impact}</dd>
          </div>
          <div>
            <dt>상태</dt>
            <dd>{exception.status}</dd>
          </div>
        </dl>
        {makeup ? (
          <div className="choice-panel">
            <h3>
              {makeup.bookedSessionId ? "예약된 보강" : "가능한 보강 수업"}
            </h3>
            {makeup.bookedSessionId ? (
              <div>
                <p>
                  {state!.classes.find(
                    (item) =>
                      item.id ===
                      state!.sessions.find(
                        (session) => session.id === makeup.bookedSessionId,
                      )?.classId,
                  )?.name ?? "연결된 수업"}
                </p>
                <label>
                  취소 이유
                  <input
                    value={makeupCancelReason}
                    onChange={(event) =>
                      setMakeupCancelReason(event.target.value)
                    }
                    placeholder="다른 시간 요청"
                  />
                </label>
                <button
                  type="button"
                  className="secondary-action"
                  disabled={mutating || makeupCancelReason.trim().length < 4}
                  onClick={() =>
                    void command({
                      type: "makeup.cancel",
                      makeupId: makeup.id,
                      reason: makeupCancelReason,
                    }).then(() => setMakeupCancelReason(""))
                  }
                >
                  이유와 함께 예약 취소
                </button>
              </div>
            ) : makeup.eligibility !== "eligible" ? (
              <p>{makeup.reason}</p>
            ) : makeup.suggestedSessionIds.length ? (
              makeup.suggestedSessionIds.map((sessionId) => {
                const session = state!.sessions.find(
                  (item) => item.id === sessionId,
                );
                const group = state!.classes.find(
                  (item) => item.id === session?.classId,
                );
                return (
                  <button
                    key={sessionId}
                    type="button"
                    className="secondary-action"
                    disabled={mutating}
                    onClick={() =>
                      void command({
                        type: "makeup.book",
                        makeupId: makeup.id,
                        sessionId,
                      })
                    }
                  >
                    {group?.name ?? sessionId} ·{" "}
                    {session ? formatDate(session.startsAt) : ""}
                  </button>
                );
              })
            ) : (
              <p>현재 빈자리 조건에 맞는 수업이 없어요.</p>
            )}
          </div>
        ) : null}
        <footer>
          <label>
            보류 이유
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="다음 사람이 이해할 수 있는 이유"
            />
          </label>
          <div>
            <button
              type="button"
              className="secondary-action"
              disabled={
                mutating ||
                reason.trim().length < 4 ||
                exception.severity === "critical"
              }
              onClick={() =>
                void command({
                  type: "exception.defer",
                  exceptionId: exception.id,
                  reason,
                }).then(() => navigate("/close/today"))
              }
            >
              이유와 함께 보류
            </button>
            <button
              type="button"
              className="primary-action"
              disabled={mutating || exception.status === "resolved"}
              onClick={() =>
                void command({
                  type: "exception.resolve",
                  exceptionId: exception.id,
                  action: "accept_suggestion",
                }).then(() => navigate("/close/today"))
              }
            >
              확인하고 마침
            </button>
          </div>
        </footer>
      </section>
    </AppShell>
  );
}

export function BillingPage() {
  return (
    <WorkspaceGate roles={["owner", "manager", "accounting", "sample_user"]}>
      <Billing />
    </WorkspaceGate>
  );
}

function Billing() {
  const { state, command, mutating } = useWorkspace();
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [kinds, setKinds] = useState<Record<string, "payment" | "refund">>({});
  const [issuing, setIssuing] = useState(false);
  const [reversingId, setReversingId] = useState<string | null>(null);
  const [reverseReason, setReverseReason] = useState("");
  const [invoiceForm, setInvoiceForm] = useState({
    studentId: state!.students[0]?.id ?? "",
    period: monthKey(new Date(), state!.tenant.timezone),
    amountKrw: "",
    discountKrw: "0",
    dueDate: businessDateKey(addDaysIso(new Date(), 7), state!.tenant.timezone),
  });
  async function issueInvoice() {
    await command({
      type: "billing.invoiceIssue",
      studentId: invoiceForm.studentId,
      period: invoiceForm.period,
      amountKrw: Number(invoiceForm.amountKrw),
      discountKrw: Number(invoiceForm.discountKrw || "0"),
      dueAt: new Date(`${invoiceForm.dueDate}T23:59:59+09:00`).toISOString(),
    });
    setIssuing(false);
    setInvoiceForm((current) => ({ ...current, amountKrw: "", discountKrw: "0" }));
  }
  const totals = state!.invoices.reduce(
    (result, invoice) => ({
      billed: result.billed + invoice.amountKrw - invoice.discountKrw,
      paid: result.paid + invoice.paidKrw - invoice.refundedKrw,
      balance: result.balance + invoice.balanceKrw,
    }),
    { billed: 0, paid: 0, balance: 0 },
  );
  return (
    <AppShell
      title="수납 대조"
      description="청구·입금·환불을 원 단위로 대조합니다"
      actions={
        <button
          type="button"
          className="primary-action"
          onClick={() => setIssuing((value) => !value)}
        >
          {issuing ? "청구 입력 닫기" : "새 청구"}
        </button>
      }
    >
      {issuing ? (
        <section className="editor-panel" aria-labelledby="invoice-issue-title">
          <header>
            <p>PG 전송 없음 · 내부 원장</p>
            <h2 id="invoice-issue-title">청구 발행</h2>
          </header>
          <div className="form-grid">
            <label>
              학생
              <select
                value={invoiceForm.studentId}
                onChange={(event) =>
                  setInvoiceForm({ ...invoiceForm, studentId: event.target.value })
                }
              >
                {state!.students.filter((student) => student.active).map((student) => (
                  <option key={student.id} value={student.id}>{student.displayName} · {student.schoolGrade}</option>
                ))}
              </select>
            </label>
            <label>
              청구 월
              <input type="month" value={invoiceForm.period} onChange={(event) => setInvoiceForm({ ...invoiceForm, period: event.target.value })} />
            </label>
            <label>
              청구액
              <input inputMode="numeric" value={invoiceForm.amountKrw} onChange={(event) => setInvoiceForm({ ...invoiceForm, amountKrw: event.target.value.replace(/[^0-9]/g, "") })} />
            </label>
            <label>
              할인
              <input inputMode="numeric" value={invoiceForm.discountKrw} onChange={(event) => setInvoiceForm({ ...invoiceForm, discountKrw: event.target.value.replace(/[^0-9]/g, "") })} />
            </label>
            <label>
              납부 기한
              <input type="date" value={invoiceForm.dueDate} onChange={(event) => setInvoiceForm({ ...invoiceForm, dueDate: event.target.value })} />
            </label>
          </div>
          <footer>
            <p>같은 학생·청구 월의 중복은 서버에서 거절합니다.</p>
            <button type="button" className="primary-action" disabled={mutating || !invoiceForm.studentId || Number(invoiceForm.amountKrw) <= 0 || Number(invoiceForm.discountKrw || "0") > Number(invoiceForm.amountKrw)} onClick={() => void issueInvoice()}>
              청구 원장에 발행
            </button>
          </footer>
        </section>
      ) : null}
      <section className="metric-row">
        <Metric
          label="실청구"
          value={`${money.format(totals.billed)}원`}
          note="청구 − 할인"
        />
        <Metric
          label="순입금"
          value={`${money.format(totals.paid)}원`}
          note="입금 − 환불"
        />
        <Metric
          label="남은 차이"
          value={`${money.format(totals.balance)}원`}
          note="마감 전 확인"
          tone={totals.balance ? "warning" : "ok"}
        />
      </section>
      <section className="billing-ledger">
        {state!.invoices.map((invoice) => {
          const student = state!.students.find(
            (item) => item.id === invoice.studentId,
          );
          const kind = kinds[invoice.id] ?? "payment";
          const amount = Number(
            amounts[invoice.id] ?? (invoice.balanceKrw || 0),
          );
          return (
            <article key={invoice.id} className="reconcile-row">
              <header>
                <div>
                  <span>{invoice.period}</span>
                  <h2>{student?.displayName ?? "학생"} 청구</h2>
                </div>
                <strong
                  className={
                    invoice.balanceKrw ? "money-difference" : "money-ok"
                  }
                >
                  {invoice.balanceKrw
                    ? `${money.format(invoice.balanceKrw)}원 차이`
                    : "대조 완료"}
                </strong>
              </header>
              <dl>
                <div>
                  <dt>청구</dt>
                  <dd>{money.format(invoice.amountKrw)}원</dd>
                </div>
                <div>
                  <dt>할인</dt>
                  <dd>−{money.format(invoice.discountKrw)}원</dd>
                </div>
                <div>
                  <dt>입금</dt>
                  <dd>−{money.format(invoice.paidKrw)}원</dd>
                </div>
                <div>
                  <dt>환불</dt>
                  <dd>+{money.format(invoice.refundedKrw)}원</dd>
                </div>
                <div>
                  <dt>잔액</dt>
                  <dd>{money.format(invoice.balanceKrw)}원</dd>
                </div>
              </dl>
              <footer>
                <label>
                  종류
                  <select
                    value={kind}
                    onChange={(event) =>
                      setKinds({
                        ...kinds,
                        [invoice.id]: event.target.value as
                          | "payment"
                          | "refund",
                      })
                    }
                  >
                    <option value="payment">수동 입금</option>
                    <option value="refund">수동 환불</option>
                  </select>
                </label>
                <label>
                  금액
                  <input
                    inputMode="numeric"
                    value={
                      amounts[invoice.id] ??
                      String(Math.max(invoice.balanceKrw, 0))
                    }
                    onChange={(event) =>
                      setAmounts({
                        ...amounts,
                        [invoice.id]: event.target.value.replace(/[^0-9]/g, ""),
                      })
                    }
                  />
                </label>
                <button
                  type="button"
                  className="primary-action"
                  disabled={
                    mutating || !Number.isSafeInteger(amount) || amount <= 0
                  }
                  onClick={() =>
                    void command({
                      type: "billing.payment",
                      invoiceId: invoice.id,
                      amountKrw: amount,
                      kind,
                    })
                  }
                >
                  {kind === "payment" ? "입금 기록" : "환불 기록"}
                </button>
              </footer>
              <p className="boundary-note">
                수동 원장 기록입니다. PG 승인·실제 계좌 입금·환불 실행을 뜻하지
                않습니다.
              </p>
            </article>
          );
        })}
      </section>
      {state!.paymentReceipts.length ? (
        <section className="paper-section">
          <header><p>원래 기록을 남기는 정정</p><h2>입금·환불 영수증</h2></header>
          <ol className="receipt-list">
            {state!.paymentReceipts.toReversed().map((receipt) => (
              <li key={receipt.id}>
                <div>
                  <strong>{receipt.kind === "payment" ? "입금" : receipt.kind === "refund" ? "환불" : "되돌림"} {money.format(receipt.amountKrw)}원</strong>
                  <span>{receipt.invoiceId} · {receipt.occurredAt}</span>
                </div>
                <b>{receipt.reversedById ? "되돌림 완료" : receipt.kind === "reversal" ? "보정 영수증" : "유효"}</b>
                {receipt.kind !== "reversal" && !receipt.reversedById ? (
                  reversingId === receipt.id ? (
                    <div className="receipt-reversal">
                      <label>되돌리는 이유<input value={reverseReason} onChange={(event) => setReverseReason(event.target.value)} /></label>
                      <button type="button" className="secondary-action" disabled={mutating || reverseReason.trim().length < 4} onClick={() => void command({ type: "billing.paymentReverse", receiptId: receipt.id, reason: reverseReason }).then(() => { setReversingId(null); setReverseReason(""); })}>이유와 함께 되돌리기</button>
                    </div>
                  ) : (
                    <button type="button" className="text-action" onClick={() => setReversingId(receipt.id)}>오입력 되돌리기</button>
                  )
                ) : null}
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </AppShell>
  );
}

export function PayrollPage() {
  return (
    <WorkspaceGate roles={["owner", "manager", "accounting", "sample_user"]}>
      <Payroll />
    </WorkspaceGate>
  );
}

function Payroll() {
  const { state, command, mutating, role } = useWorkspace();
  const canApprove = role === "owner" || role === "sample_user";
  const total = state!.compensation.reduce(
    (sum, item) => sum + item.amountKrw,
    0,
  );
  const approved = state!.compensation
    .filter((item) => item.state === "approved")
    .reduce((sum, item) => sum + item.amountKrw, 0);
  return (
    <AppShell
      title="강사 수당 후보"
      description="실제 수업 사건과 규칙 버전으로 후보만 대조합니다"
    >
      <section className="metric-row">
        <Metric
          label="후보 합계"
          value={`${money.format(total)}원`}
          note="송금 전"
        />
        <Metric
          label="승인한 후보"
          value={`${money.format(approved)}원`}
          note="내부 확인 완료"
        />
        <Metric
          label="규칙"
          value={state!.compensation[0]?.ruleVersion ?? "없음"}
          note="과거 규칙을 덮지 않음"
        />
      </section>
      <section className="data-sheet">
        <div className="data-sheet__head payroll-head">
          <span>강사·수업</span>
          <span>계산 근거</span>
          <span>후보 금액</span>
          <span>상태</span>
          <span>결정</span>
        </div>
        {state!.compensation.map((candidate) => {
          const member = state!.teamMembers.find(
            (item) => item.id === candidate.instructorId,
          );
          const session = state!.sessions.find(
            (item) => item.id === candidate.sessionId,
          );
          const group = state!.classes.find(
            (item) => item.id === session?.classId,
          );
          return (
            <article className="payroll-head" key={candidate.id}>
              <span>
                <strong>{member?.displayName ?? candidate.instructorId}</strong>
                <small>{group?.name ?? candidate.sessionId}</small>
              </span>
              <span>
                {candidate.reason}
                <small>{candidate.ruleVersion}</small>
              </span>
              <span>
                <strong>{money.format(candidate.amountKrw)}원</strong>
              </span>
              <span>{candidate.state === "approved" ? "승인됨" : "후보"}</span>
              <span>
                {canApprove ? (
                  <button
                    type="button"
                    className="secondary-action"
                    disabled={mutating || candidate.state === "approved"}
                    onClick={() =>
                      void command({
                        type: "payroll.approve",
                        candidateId: candidate.id,
                      })
                    }
                  >
                    {candidate.state === "approved"
                      ? "확인 완료"
                      : "사건 대조 후 승인"}
                  </button>
                ) : (
                  <small>원장 승인 대기</small>
                )}
              </span>
            </article>
          );
        })}
      </section>
      <p className="boundary-note boundary-note--large">
        이 화면은 내부 후보 원장입니다. 근로자성·세금·급여의 법적 판단이나
        송금을 대신하지 않습니다.
      </p>
    </AppShell>
  );
}

export function RecordsPage() {
  return (
    <WorkspaceGate roles={["owner", "manager", "accounting", "desk", "read_only", "sample_user"]}>
      <Records />
    </WorkspaceGate>
  );
}

function Records() {
  const { state, role } = useWorkspace();
  const entries = [...state!.auditLog].toReversed();
  return (
    <AppShell
      title="기록"
      description="수정·외부 효과·마감을 사건별로 남깁니다"
      actions={
        role === "owner" || role === "sample_user" ? <a className="secondary-action" href="/api/export" download>
          전체 기록 내려받기
        </a> : null
      }
    >
      <section className="records-layout">
        <article className="paper-section">
          <header>
            <p>변경 영수증</p>
            <h2>감사 기록</h2>
          </header>
          {entries.length ? (
            <ol className="timeline-list">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <time>{formatDate(entry.occurredAt)}</time>
                  <div>
                    <strong>{entry.summary}</strong>
                    <span>
                      {entry.action} · {entry.actorId}
                    </span>
                  </div>
                  <b>{entry.id.slice(-8)}</b>
                </li>
              ))}
            </ol>
          ) : (
            <EmptyInline text="새 변경 기록이 생기면 여기에 나타납니다." />
          )}
        </article>
        <aside className="record-sidebar">
          <section>
            <span>마감 영수증</span>
            <strong>{state!.closeSnapshots.length}건</strong>
            <Link href="/close/week">주·월 마감 보기</Link>
          </section>
          <section>
            <span>외부 효과</span>
            <strong>{state!.effectReceipts.length}건</strong>
            <p>전달·결제·마감을 상태별로 구분</p>
          </section>
          <section>
            <span>보호자 초안</span>
            <strong>{state!.guardianDrafts.length}건</strong>
            <Link href="/guardian-drafts">검토하기</Link>
          </section>
        </aside>
      </section>
    </AppShell>
  );
}

export function GuardianDraftsPage() {
  return (
    <WorkspaceGate roles={["owner", "manager", "sample_user"]}>
      <GuardianDrafts />
    </WorkspaceGate>
  );
}

function GuardianDrafts() {
  const { state, command, makeGuardianLink, mutating } = useWorkspace();
  const [links, setLinks] = useState<Record<string, string>>({});
  async function link(draftId: string) {
    const result = await makeGuardianLink(draftId);
    setLinks((current) => ({ ...current, [draftId]: result.url }));
  }
  return (
    <AppShell
      title="보호자 안내"
      description="내용 검토와 실제 전달 상태를 분리합니다"
      actions={
        <Link className="secondary-action" href="/records">
          기록으로
        </Link>
      }
    >
      <section className="message-stack">
        {state!.guardianDrafts.length ? (
          state!.guardianDrafts.map((draft) => {
            const student = state!.students.find(
              (item) => item.id === draft.studentId,
            );
            return (
              <article className="message-proof" key={draft.id}>
                <header>
                  <div>
                    <span>
                      {draft.purpose} · {draft.recipientMasked}
                    </span>
                    <h2>{student?.displayName ?? "학생"} 보호자 안내</h2>
                  </div>
                  <strong>{receiptLabel(draft.receiptState)}</strong>
                </header>
                <blockquote>{draft.body}</blockquote>
                <dl>
                  <div>
                    <dt>검토</dt>
                    <dd>
                      {draft.requiresReview ? "원장 확인 필요" : "내용 확인됨"}
                    </dd>
                  </div>
                  <div>
                    <dt>휴식 시간</dt>
                    <dd>
                      {draft.quietHoursBlocked ? "발송 차단" : "차단 없음"}
                    </dd>
                  </div>
                  <div>
                    <dt>링크</dt>
                    <dd>
                      {draft.capabilityId
                        ? "목적 제한 링크 생성됨"
                        : "아직 없음"}
                    </dd>
                  </div>
                </dl>
                <footer>
                  {draft.requiresReview ? (
                    <button
                      type="button"
                      className="primary-action"
                      disabled={mutating}
                      onClick={() =>
                        void command({
                          type: "guardian.draftApprove",
                          draftId: draft.id,
                        })
                      }
                    >
                      문구 확인
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="primary-action"
                      disabled={mutating || Boolean(draft.capabilityId)}
                      onClick={() => void link(draft.id)}
                    >
                      {draft.capabilityId
                        ? "링크 생성됨"
                        : "72시간 확인 링크 만들기"}
                    </button>
                  )}
                  {links[draft.id] ? (
                    <div className="copy-link">
                      <code>
                        {window.location.origin}
                        {links[draft.id]}
                      </code>
                      <Link href={links[draft.id]!}>이 기기에서 열기</Link>
                    </div>
                  ) : null}
                  <span>메시지 공급자 비활성 · 자동 발송 안 함</span>
                </footer>
              </article>
            );
          })
        ) : (
          <Empty
            title="검토할 안내가 없어요"
            body="결석·수납·학습 사건에서 생긴 초안이 이곳에 모입니다."
            href="/classes"
          />
        )}
      </section>
    </AppShell>
  );
}

export function PeriodClosePage({ period }: { period: "week" | "month" }) {
  return (
    <WorkspaceGate roles={["owner", "manager", "sample_user"]}>
      <PeriodClose period={period} />
    </WorkspaceGate>
  );
}

function PeriodClose({ period }: { period: "week" | "month" }) {
  const { state, command, mutating } = useWorkspace();
  const open = unresolvedExceptions(state!);
  const key =
    period === "week"
      ? isoWeekKey(new Date(), state!.tenant.timezone)
      : monthKey(new Date(), state!.tenant.timezone);
  const snapshot = state!.closeSnapshots
    .filter((item) => item.period === period && item.periodKey === key)
    .at(-1);
  return (
    <AppShell
      title={period === "week" ? "이번 주 마감" : "이번 달 마감"}
      description="숫자마다 분모·기간·원인 기록을 함께 봅니다"
      actions={
        <div className="period-switch">
          <Link
            className={period === "week" ? "is-current" : ""}
            href="/close/week"
          >
            주
          </Link>
          <Link
            className={period === "month" ? "is-current" : ""}
            href="/close/month"
          >
            월
          </Link>
        </div>
      }
    >
      <section className="signal-grid">
        {state!.signals.map((signal) => (
          <article
            key={signal.id}
            className={`signal-card signal-card--${signal.state}`}
          >
            <header>
              <span>{signal.period}</span>
              <strong>
                {signal.state === "unknown"
                  ? "판단할 수 없음"
                  : signal.state === "observed"
                    ? "확인 필요"
                    : "안정"}
              </strong>
            </header>
            <h2>{signal.title}</h2>
            <div className="signal-fraction">
              <b>{signal.numerator}</b>
              <span>/ {signal.denominator}</span>
            </div>
            <p>{signal.explanation}</p>
            <footer>
              근거 {signal.sourceIds.length}건 · 갱신{" "}
              {formatDate(signal.staleAt)}
            </footer>
          </article>
        ))}
      </section>
      <section className="period-close-panel">
        <div>
          <p>{key}</p>
          <h2>
            {snapshot && !snapshot.reopenedAt
              ? "마감 영수증이 있어요."
              : open.length
                ? `오늘 남은 일 ${open.length}건이 있어요.`
                : "기간 마감을 만들 수 있어요."}
          </h2>
          <span>
            표본이 작거나 기록이 빠진 신호는 위험 점수로 바꾸지 않습니다.
          </span>
        </div>
        {snapshot && !snapshot.reopenedAt ? (
          <strong>
            revision {snapshot.revision} · {formatDate(snapshot.completedAt)}
          </strong>
        ) : (
          <button
            type="button"
            className="primary-action"
            disabled={mutating || open.length > 0}
            onClick={() =>
              void command({ type: "close.complete", period, periodKey: key })
            }
          >
            {open.length
              ? "오늘 남은 일부터 마치기"
              : `${period === "week" ? "이번 주" : "이번 달"} 마감`}
          </button>
        )}
      </section>
      <PortfolioThemeControl />
    </AppShell>
  );
}

function Metric({
  label,
  value,
  note,
  tone = "neutral",
}: {
  label: string;
  value: string;
  note: string;
  tone?: "neutral" | "warning" | "ok";
}) {
  return (
    <article className={`metric-card metric-card--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}
function Empty({
  title,
  body,
  href,
}: {
  title: string;
  body: string;
  href: string;
}) {
  return (
    <section className="empty-state">
      <h2>{title}</h2>
      <p>{body}</p>
      <Link className="primary-action" href={href}>
        돌아가기
      </Link>
    </section>
  );
}
function EmptyInline({ text }: { text: string }) {
  return <p className="empty-inline">{text}</p>;
}
function formatDate(value: string) {
  return new Intl.DateTimeFormat("ko-KR", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
function attendanceLabel(
  status: WorkspaceState["attendance"][number]["status"],
) {
  return {
    unmarked: "기록 전",
    present: "출석",
    absent: "결석",
    late: "지각",
    left_early: "조퇴",
  }[status];
}
function exceptionKind(kind: ExceptionCase["kind"]) {
  return {
    security: "보안",
    attendance: "출결",
    makeup: "보강",
    guardian: "보호자",
    billing: "수납",
    payroll: "수당",
    support: "지원",
  }[kind];
}
function receiptLabel(
  state: WorkspaceState["guardianDrafts"][number]["receiptState"],
) {
  return {
    draft: "초안",
    pending: "전달 대기",
    accepted: "검토 완료",
    delivered: "전달 확인",
    failed: "전달 실패",
    not_applicable: "외부 효과 없음",
  }[state];
}
export { roleLabels };
